// components/CompanyTableControls.jsx
import React from "react";
import {
  Stack,
  TextField,
  MenuItem,
  Button,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Deselect as DeselectIcon,
} from "@mui/icons-material";

const CompanyTableControls = ({
  searchTerm,
  onSearchChange,
  approvalFilter,
  onApprovalFilterChange,
  onExportClick,
  onImportClick,
  onDeleteClick,
  hasSelectedCompanies,
  isSmall,
  showInactive,
  onShowInactiveChange,
  onClearSearch,
  onRerunClick,
  onExtractAllClick,
  onClearSelectionClick,
}) => {
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 11,
        backgroundColor: "background.paper",
        pt: 0.5,
        pb: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            width: { xs: "100%", sm: "auto" },
            gap: 1,
            order: { xs: 1, sm: 1 },
          }}
        >
          <TextField
            placeholder="Search company..."
            value={searchTerm}
            onChange={onSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 300 },
            }}
          />
          {searchTerm && (
            <IconButton
              onClick={onClearSearch}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                },
              }}
              title="Clear search"
            >
              <ClearIcon />
            </IconButton>
          )}
          <Button
            variant="outlined"
            color="warning"
            onClick={onClearSelectionClick}
            size="small"
            startIcon={<DeselectIcon />}
            sx={{
              flex: { xs: "1 1 100%", sm: "0 1 auto" },
              whiteSpace: "nowrap",
            }}
          >
            Clear Selection
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            order: { xs: 2, sm: 2 },
            flexWrap: "wrap",
            position: "relative",
            zIndex: 12,
            backgroundColor: "background.paper",
            borderRadius: 1,
            minWidth: 150,
          }}
        >
          <TextField
            select
            label="Filter by Approval"
            size="small"
            value={approvalFilter}
            onChange={onApprovalFilterChange}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="unapproved">Unapproved</MenuItem>
          </TextField>

          {/* Add this new TextField for Show Inactive filter */}
          <TextField
            select
            label="Show Inactive"
            size="small"
            value={showInactive}
            onChange={onShowInactiveChange}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="no">No</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
          </TextField>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            order: { xs: 3, sm: 3 },
            justifyContent: { xs: "stretch", sm: "flex-end" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={onDeleteClick}
            disabled={!hasSelectedCompanies}
            size="small"
            startIcon={<DeleteIcon />}
            sx={{
              flex: { xs: "1 1 calc(50% - 4px)", sm: "0 1 auto" },
              minWidth: { xs: "120px", sm: "auto" },
              whiteSpace: "nowrap",
            }}
          >
            Delete
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={onExtractAllClick}
            size="small"
            sx={{
              flex: { xs: "1 1 calc(50% - 4px)", sm: "0 1 auto" },
              minWidth: { xs: "120px", sm: "auto" },
              whiteSpace: "nowrap",
            }}
          >
            Extract All Data
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={onRerunClick}
            disabled={!hasSelectedCompanies}
            size="small"
            startIcon={<RefreshIcon />}
            sx={{
              flex: { xs: "1 1 calc(50% - 4px)", sm: "0 1 auto" },
              minWidth: { xs: "120px", sm: "auto" },
              whiteSpace: "nowrap",
            }}
          >
            Re-run
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              console.log("Import button clicked");
              onImportClick();
            }}
            size="small"
            startIcon={<UploadIcon />}
            sx={{
              flex: { xs: "1 1 calc(50% - 4px)", sm: "0 1 auto" },
              minWidth: { xs: "120px", sm: "auto" },
              whiteSpace: "nowrap",
            }}
          >
            Import Data
          </Button>
          <Button
            variant="contained"
            onClick={onExportClick}
            disabled={!hasSelectedCompanies}
            size="small"
            sx={{
              flex: { xs: "1 1 calc(50% - 4px)", sm: "0 1 auto" },
              minWidth: { xs: "120px", sm: "auto" },
              whiteSpace: "nowrap",
            }}
          >
            Export Data
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyTableControls;
